import { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import dayjs, { Dayjs } from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  IP_FILTER_OPTIONS,
  MIN_SCORE_FILTER_OPTIONS,
  MULTI_BRAND_FILTER_OPTIONS,
  QUALIFIED_FILTER_OPTIONS,
} from "../constants";
import LeadsMobileDateRangeDrawer from "./LeadsMobileDateRangeDrawer";
import IconWithIndicator from "./IconWithIndicator";
import { colors, spacing, typography } from "../../../../assets/styles/tokens";

const FILTER_FIELD_WIDTH = 160;
// About 4½ options, so a scrollable list visibly continues.
const FILTER_MENU_MAX_HEIGHT = 216;

export function FilterRow({
  label,
  value,
  onChange,
  options,
  disabled,
  title,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  title?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.Spacing.xl.$value,
      }}
      title={title}
    >
      <Typography
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Regular.$value,
          fontSize: typography.Size.Text.sm.$value,
          lineHeight: typography.Line_height.text.sm.$value,
          color: colors.GrayLightMode[600].$value,
        }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        size="small"
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={{
          // Same width as the field and short enough to always open right
          // below it — a longer list (e.g. Brand) would otherwise not fit
          // under the field and get shifted up over it. Longer lists scroll.
          PaperProps: {
            sx: {
              width: FILTER_FIELD_WIDTH,
              maxHeight: FILTER_MENU_MAX_HEIGHT,
            },
          },
          MenuListProps: {
            sx: {
              "& .MuiMenuItem-root:hover": {
                backgroundColor: colors.Brand[25].$value,
              },
              "& .MuiMenuItem-root.Mui-selected": {
                backgroundColor: colors.Brand[50].$value,
                "&:hover": { backgroundColor: colors.Brand[50].$value },
              },
            },
          },
        }}
        sx={{
          // Fixed (not min) width so every row's field lines up, even when a
          // long brand name is selected — MUI's Select already ellipsizes
          // the value text that doesn't fit.
          width: FILTER_FIELD_WIDTH,
          flexShrink: 0,
          borderRadius: spacing.Radius.md.$value,
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[900].$value,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.GrayLightMode[200].$value,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.Brand[600].$value,
          },
        }}
      >
        {options.map((option) => (
          // A name too long for the field-width menu ends in "…"; the full
          // name shows on long-press/hover.
          <MenuItem
            key={option.value}
            value={option.value}
            title={option.label}
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

interface LeadsMobileFilterBarProps {
  data: LeadsManagementData;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function formatDisplayDate(value: string): string {
  const parsed = toDayjs(value);
  return parsed ? parsed.format("DD/MM/YYYY") : "—";
}

// Mobile's filter icon condenses the desktop LeadsFilterItems filters
// (Brand Name dropdown, plus Qualified By Brand/IP Address/Multi Brand from
// the shared FilterComponent) into a simple bottom drawer of dropdown
// groups — same request params, lighter-weight UI for a narrow screen.
export default function LeadsMobileFilterBar({
  data,
}: LeadsMobileFilterBarProps) {
  const {
    start,
    end,
    setStart,
    setEnd,
    search,
    setSearch,
    brand,
    brandOptions,
    setBrand,
    qualified,
    setQualified,
    ipFilter,
    setIpFilter,
    multiBrand,
    setMultiBrand,
    isLoading,
  } = data;

  const [dateDrawerOpen, setDateDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // The drawer's own draft state — Brand/Qualified/IP/Multi Brand only take
  // effect on "Apply" (matching the Figma), rather than filtering on every
  // dropdown change like the date range's own draft/Apply flow above.
  const [draftBrand, setDraftBrand] = useState(brand);
  const [draftQualified, setDraftQualified] = useState(qualified);
  const [draftIpFilter, setDraftIpFilter] = useState(ipFilter);
  const [draftMultiBrand, setDraftMultiBrand] = useState(multiBrand);

  // Every filter here uses "All" as its unset value.
  const countSet = (values: string[]) =>
    values.filter((value) => value !== "All").length;
  const appliedFilterCount = countSet([brand, qualified, ipFilter, multiBrand]);
  const activeDraftFilterCount = countSet([
    draftBrand,
    draftQualified,
    draftIpFilter,
    draftMultiBrand,
  ]);
  const draftChanged =
    draftBrand !== brand ||
    draftQualified !== qualified ||
    draftIpFilter !== ipFilter ||
    draftMultiBrand !== multiBrand;

  function openFilterDrawer() {
    setDraftBrand(brand);
    setDraftQualified(qualified);
    setDraftIpFilter(ipFilter);
    setDraftMultiBrand(multiBrand);
    setFilterDrawerOpen(true);
  }

  function handleClearAllDraftFilters() {
    setDraftBrand("All");
    setDraftQualified("All");
    setDraftIpFilter("All");
    setDraftMultiBrand("All");
  }

  function applyFilters() {
    setBrand(draftBrand);
    setQualified(draftQualified);
    setIpFilter(draftIpFilter);
    setMultiBrand(draftMultiBrand);
    setFilterDrawerOpen(false);
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: spacing.Spacing.md.$value,
        mt: spacing.Spacing.lg.$value,
      }}
    >
      <Button
        onClick={() => setDateDrawerOpen(true)}
        endIcon={<KeyboardArrowDownIcon />}
        disabled={isLoading}
        sx={{
          flex: 1,
          height: 44,
          justifyContent: "space-between",
          border: `1px solid ${colors.GrayLightMode[200].$value}`,
          borderRadius: spacing.Radius.md.$value,
          px: spacing.Spacing.lg.$value,
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.sm.$value,
          lineHeight: typography.Line_height.text.sm.$value,
          letterSpacing: "0%",
          verticalAlign: "middle",
          color: colors.GrayLightMode[700].$value,
          textTransform: "none",
        }}
      >
        {formatDisplayDate(start)} - {formatDisplayDate(end)}
      </Button>

      <IconButton
        onClick={() => setSearchDrawerOpen(true)}
        sx={{ width: 44, height: 44, borderRadius: spacing.Radius.md.$value }}
      >
        <IconWithIndicator active={search !== ""}>
          <SearchIcon
            fontSize="medium"
            sx={{ color: colors.GrayLightMode[700].$value }}
          />
        </IconWithIndicator>
      </IconButton>

      <IconButton
        onClick={openFilterDrawer}
        sx={{
          width: 44,
          height: 44,
          borderRadius: spacing.Radius.md.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        <IconWithIndicator active={appliedFilterCount > 0}>
          <TuneIcon fontSize="medium" />
        </IconWithIndicator>
      </IconButton>

      <LeadsMobileDateRangeDrawer
        open={dateDrawerOpen}
        onClose={() => setDateDrawerOpen(false)}
        start={start}
        end={end}
        onApply={(nextStart, nextEnd) => {
          setStart(nextStart);
          setEnd(nextEnd);
        }}
      />

      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: spacing.Spacing.lg.$value,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: spacing.Radius.md.$value,
              bgcolor: colors.GrayLightMode[200].$value,
            }}
          />
        </Box>

        <Box
          sx={{
            px: spacing.Spacing["2xl"].$value,
            pt: spacing.Spacing.lg.$value,
            pb: spacing.Spacing["2xl"].$value,
            display: "flex",
            flexDirection: "column",
            gap: spacing.Spacing.xl.$value,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.md.$value,
                color: colors.GrayLightMode[900].$value,
              }}
            >
              Filter by
            </Typography>
            <Typography
              onClick={
                activeDraftFilterCount > 0
                  ? handleClearAllDraftFilters
                  : undefined
              }
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.sm.$value,
                cursor: activeDraftFilterCount > 0 ? "pointer" : "default",
              }}
            >
              Clear All
              {activeDraftFilterCount > 0 ? ` (${activeDraftFilterCount})` : ""}
            </Typography>
          </Box>

          <FilterRow
            label="Brand"
            value={draftBrand}
            onChange={setDraftBrand}
            options={brandOptions}
          />
          <FilterRow
            label="Qualified By Brand"
            value={draftQualified}
            onChange={setDraftQualified}
            options={QUALIFIED_FILTER_OPTIONS}
          />
          <FilterRow
            label="IP Address"
            value={draftIpFilter}
            onChange={setDraftIpFilter}
            options={IP_FILTER_OPTIONS}
          />
          <FilterRow
            label="Multi Brand"
            value={draftMultiBrand}
            onChange={setDraftMultiBrand}
            options={MULTI_BRAND_FILTER_OPTIONS}
          />
          <FilterRow
            label="Minimum Lead Score"
            value="0"
            onChange={() => {}}
            options={MIN_SCORE_FILTER_OPTIONS}
            disabled
            title="Coming soon — pending backend support"
          />

          <Button
            fullWidth
            variant="contained"
            onClick={applyFilters}
            disabled={!draftChanged}
            sx={{
              mt: spacing.Spacing.md.$value,
              borderRadius: spacing.Radius.md.$value,
              bgcolor: colors.Brand[600].$value,
              "&:hover": { bgcolor: colors.Brand[700].$value },
              "&.Mui-disabled": {
                bgcolor: colors.GrayLightMode[100].$value,
                color: colors.GrayLightMode[400].$value,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={searchDrawerOpen}
        onClose={() => setSearchDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: spacing.Radius.lg.$value,
              borderTopRightRadius: spacing.Radius.lg.$value,
              p: spacing.Spacing.lg.$value,
            },
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: spacing.Radius.md.$value,
            bgcolor: colors.GrayLightMode[300].$value,
            mx: "auto",
            mb: spacing.Spacing.lg.$value,
          }}
        />
        <Typography
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            color: colors.GrayLightMode[900].$value,
            mb: spacing.Spacing.md.$value,
          }}
        >
          Search Leads
        </Typography>
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder="Name, email, user unique ID, ID or IP"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: colors.Brand[600].$value,
              },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: colors.GrayLightMode[500].$value }}
                />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Drawer>
    </Box>
  );
}
